/**
 * OPFS blob storage for captured media.
 *
 * We keep binary payloads (photos, audio, signatures) as flat files under
 * `/media/<sessionId>/<captureId>.<ext>` in the Origin Private File System.
 * The SQLite row in `captures` carries the path so the sync queue can stream
 * the blob straight from disk without loading it into memory.
 */

const ROOT_DIR = 'media';

async function getRoot(): Promise<FileSystemDirectoryHandle> {
  if (typeof navigator === 'undefined' || !('storage' in navigator) || !navigator.storage.getDirectory) {
    throw new Error('OPFS is not supported in this environment');
  }
  const opfs = await navigator.storage.getDirectory();
  return opfs.getDirectoryHandle(ROOT_DIR, { create: true });
}

async function getSessionDir(sessionId: string): Promise<FileSystemDirectoryHandle> {
  const root = await getRoot();
  return root.getDirectoryHandle(sessionId, { create: true });
}

export interface SaveOptions {
  sessionId: string;
  captureId: string;
  extension: string;
  blob: Blob;
}

export async function saveMediaBlob(opts: SaveOptions): Promise<string> {
  const dir = await getSessionDir(opts.sessionId);
  const filename = `${opts.captureId}.${opts.extension}`;
  const fileHandle = await dir.getFileHandle(filename, { create: true });

  // createWritable isn't available in workers on every target we care about —
  // fall back to the sync access handle API where we can.
  if ('createWritable' in fileHandle) {
    const writable = await fileHandle.createWritable({ keepExistingData: false });
    await writable.write(opts.blob);
    await writable.close();
  } else {
    const buffer = await opts.blob.arrayBuffer();
    // @ts-expect-error — sync handle is available in workers and some browsers
    const sync = await fileHandle.createSyncAccessHandle();
    try {
      sync.truncate(0);
      sync.write(buffer);
      sync.flush();
    } finally {
      sync.close();
    }
  }

  return `${ROOT_DIR}/${opts.sessionId}/${filename}`;
}

export async function readMediaBlob(path: string): Promise<Blob> {
  const segments = path.split('/').filter(Boolean);
  if (segments[0] !== ROOT_DIR || segments.length !== 3) {
    throw new Error(`Invalid media path: ${path}`);
  }
  const [, sessionId, filename] = segments;
  const dir = await getSessionDir(sessionId as string);
  const fileHandle = await dir.getFileHandle(filename as string);
  return fileHandle.getFile();
}

export async function deleteMediaBlob(path: string): Promise<void> {
  const segments = path.split('/').filter(Boolean);
  if (segments[0] !== ROOT_DIR || segments.length !== 3) return;
  const [, sessionId, filename] = segments;
  const dir = await getSessionDir(sessionId as string);
  await dir.removeEntry(filename as string).catch(() => {
    // already gone — no-op
  });
}

export async function totalMediaBytes(): Promise<number> {
  const estimate = await navigator.storage.estimate();
  return estimate.usage ?? 0;
}
